import { ISSUE_STATUS_LABELS, ISSUE_TYPE_LABELS, type IssueStatus } from '@company/shared'
import {
  X,
  Edit,
  Clock,
  Calendar,
  User,
  Flag,
  Tag,
  History,
  MessageSquare,
  GitBranch,
  Workflow,
  FileText,
} from 'lucide-react'
import { useState } from 'react'

import { PriorityBadge } from '@/components/common/priority-badge'
import { CommentList } from '@/components/pm/comment/comment-list'
import { LinkedDocumentsTab } from '@/components/pm/issue/linked-documents-tab'
import { StageAssignmentPanel } from '@/components/pm/issue/stage-assignment-panel'
import { WorkflowPanel } from '@/components/pm/workflow'
import {
  useAuth,
  useEmployee,
  useIssue,
  useIssueHistory,
  useIssueChildren,
  useUpdateIssueStatus,
  useProject,
  useTeam,
} from '@/hooks'
import { cn } from '@/lib/utils'

import { IssueForm } from './issue-form'

interface IssueDetailProps {
  issueId: string
  onClose: () => void
}

const STATUS_COLORS: Record<IssueStatus, string> = {
  backlog: 'bg-gray-100 text-gray-800',
  todo: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  code_review: 'bg-purple-100 text-purple-800',
  testing: 'bg-orange-100 text-orange-800',
  done: 'bg-green-100 text-green-800',
  blocked: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
}

// PRIORITY_COLORS removed in favor of PriorityBadge

/**
 * 任务详情抽屉组件
 */
export function IssueDetail({ issueId, onClose }: IssueDetailProps) {
  const [showEditForm, setShowEditForm] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'detail' | 'comments' | 'history' | 'children' | 'stages' | 'workflow' | 'documents'
  >('detail')

  const { data: issue, isLoading, error } = useIssue(issueId)
  const { data: project } = useProject(issue?.projectId ?? '')
  const { data: team } = useTeam(project?.teamId ?? '')
  const { data: assignee } = useEmployee(issue?.assigneeId ?? '')
  const { data: history } = useIssueHistory(issueId)
  const { data: children } = useIssueChildren(issueId)
  const { user } = useAuth()
  const updateStatusMutation = useUpdateIssueStatus()

  // 检查当前用户是否是团队组长或有权限修改阶段审核人
  const isTeamLeader =
    team?.leaderId === user?.id || user?.role === 'director' || user?.role === 'pm'

  const handleStatusChange = (status: IssueStatus) => {
    updateStatusMutation.mutate({ id: issueId, data: { status } })
  }

  if (isLoading) {
    return (
      <div className="bg-background fixed inset-y-0 right-0 z-50 w-full max-w-xl shadow-lg">
        <div className="flex h-full items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      </div>
    )
  }

  if (error || !issue) {
    return (
      <div className="bg-background fixed inset-y-0 right-0 z-50 w-full max-w-xl shadow-lg">
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <div className="text-destructive">任务不存在或加载失败</div>
          <button onClick={onClose} className="text-primary text-sm hover:underline">
            关闭
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* 背景遮罩 */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />

      {/* 抽屉 */}
      <div className="bg-background fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto shadow-lg">
        {/* 头部 */}
        <div className="bg-background sticky top-0 z-10 border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground font-mono text-sm">{issue.code}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium',
                  STATUS_COLORS[issue.status]
                )}
              >
                {ISSUE_STATUS_LABELS[issue.status]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditForm(true)}
                className="hover:bg-muted rounded-md p-2"
                title="编辑"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button onClick={onClose} className="hover:bg-muted rounded-md p-2">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <h2 className="mt-2 text-xl font-semibold">{issue.title}</h2>
        </div>

        {/* Tab 导航 */}
        <div className="border-b px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('detail')}
              className={cn(
                'border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'detail'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              详情
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={cn(
                'flex items-center gap-1 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'comments'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              <MessageSquare className="h-4 w-4" />
              评论
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={cn(
                'border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'history'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              历史 ({history?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('children')}
              className={cn(
                'border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'children'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              子任务 ({children?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('stages')}
              className={cn(
                'flex items-center gap-1 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'stages'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              <GitBranch className="h-4 w-4" />
              阶段分配
            </button>
            <button
              onClick={() => setActiveTab('workflow')}
              className={cn(
                'flex items-center gap-1 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'workflow'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              <Workflow className="h-4 w-4" />
              工作流
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={cn(
                'flex items-center gap-1 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'documents'
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              <FileText className="h-4 w-4" />
              文档
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="min-w-0 p-6">
          {activeTab === 'detail' && (
            <div className="space-y-6">
              {/* 描述 */}
              {issue.description && (
                <div>
                  <h3 className="text-muted-foreground mb-2 text-sm font-medium">描述</h3>
                  <p className="whitespace-pre-wrap text-sm">{issue.description}</p>
                </div>
              )}

              {/* 属性 */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-3">
                  <Tag className="text-muted-foreground h-4 w-4" />
                  <div>
                    <p className="text-muted-foreground text-xs">类型</p>
                    <p className="text-sm font-medium">{ISSUE_TYPE_LABELS[issue.type]}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Flag className="text-muted-foreground h-4 w-4" />
                  <div>
                    <p className="text-muted-foreground text-xs">优先级</p>
                    <div className="mt-0.5">
                      <PriorityBadge priority={issue.priority} />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <User className="text-muted-foreground h-4 w-4" />
                  <div>
                    <p className="text-muted-foreground text-xs">负责人</p>
                    <p className="text-sm font-medium">
                      {issue.assigneeId
                        ? assignee?.name || `员工 ${issue.assigneeId.slice(0, 8)}`
                        : '未分配'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="text-muted-foreground h-4 w-4" />
                  <div>
                    <p className="text-muted-foreground text-xs">截止日期</p>
                    <p className="text-sm font-medium">
                      {issue.dueDate
                        ? new Date(issue.dueDate).toLocaleDateString('zh-CN')
                        : '未设置'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="text-muted-foreground h-4 w-4" />
                  <div>
                    <p className="text-muted-foreground text-xs">预估/实际工时</p>
                    <p className="text-sm font-medium">
                      {issue.estimatedHours || 0}h / {issue.actualHours || 0}h
                    </p>
                  </div>
                </div>
              </div>

              {/* 状态更新 */}
              <div>
                <h3 className="text-muted-foreground mb-2 text-sm font-medium">更新状态</h3>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(ISSUE_STATUS_LABELS) as IssueStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      disabled={issue.status === status || updateStatusMutation.isPending}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
                        issue.status === status
                          ? STATUS_COLORS[status]
                          : 'bg-muted hover:bg-muted/80'
                      )}
                    >
                      {ISSUE_STATUS_LABELS[status]}
                    </button>
                  ))}
                </div>
              </div>

              {/* 阻塞原因 */}
              {issue.status === 'blocked' && issue.blockedReason && (
                <div className="rounded-md bg-red-50 p-3">
                  <h3 className="mb-1 text-sm font-medium text-red-800">阻塞原因</h3>
                  <p className="text-sm text-red-700">{issue.blockedReason}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && <CommentList issueId={issueId} />}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {history && history.length > 0 ? (
                history.map((entry) => (
                  <div key={entry.id} className="flex gap-3 border-b pb-4 last:border-0">
                    <History className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="text-sm">
                        <span className="font-medium">{entry.field}</span> 从{' '}
                        <span className="text-muted-foreground">{entry.oldValue || '空'}</span> 改为{' '}
                        <span className="font-medium">{entry.newValue || '空'}</span>
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {new Date(entry.createdAt).toLocaleString('zh-CN')}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground text-center text-sm">暂无历史记录</div>
              )}
            </div>
          )}

          {activeTab === 'children' && (
            <div className="space-y-2">
              {children && children.length > 0 ? (
                children.map((child) => (
                  <div
                    key={child.id}
                    className="flex items-center justify-between rounded-md border p-3"
                  >
                    <div>
                      <span className="text-muted-foreground mr-2 font-mono text-xs">
                        {child.code}
                      </span>
                      <span className="text-sm font-medium">{child.title}</span>
                    </div>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_COLORS[child.status]
                      )}
                    >
                      {ISSUE_STATUS_LABELS[child.status]}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground text-center text-sm">暂无子任务</div>
              )}
            </div>
          )}

          {activeTab === 'stages' && (
            <StageAssignmentPanel issueId={issueId} projectId={issue.projectId} />
          )}

          {activeTab === 'workflow' && (
            <WorkflowPanel
              issueId={issueId}
              issueTitle={issue.title}
              teamId={project?.teamId}
              isTeamLeader={isTeamLeader}
            />
          )}

          {activeTab === 'documents' && (
            <LinkedDocumentsTab issueId={issueId} projectId={issue.projectId} />
          )}
        </div>
      </div>

      {/* 编辑表单 */}
      {showEditForm && (
        <IssueForm
          issue={issue}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => setShowEditForm(false)}
        />
      )}
    </>
  )
}
